import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import {
  type AuditPage,
  artistsSummary,
  fetchAudit,
  searchState,
  searchStateLabel,
  lastCheckedLabel,
} from "../lib/auditPage";
import { formatPacificDate, platformLabel } from "../lib/format";

/**
 * The read-only view of what the scraper and the filters have done, ported from
 * `server/pages/audit.py`.
 *
 * Three collapsed sections: an artist table can run to well over a hundred
 * rows and would bury everything below it. The artist table is open by
 * default because it is the one thing here you cannot see anywhere else in the
 * app.
 *
 * The "Last search" column is NOT "when did we last look at this band", and it
 * is labelled carefully because the obvious reading is wrong in three ways at
 * once -- see `searchState` in the lib. A first pass at this page called it
 * "Last checked" and flagged anything over a week in amber, which turned a
 * normal rotation into 53 rows of false alarm.
 */

function Section({
  title,
  subtitle,
  open,
  children,
}: {
  title: string;
  subtitle?: string;
  open?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details open={open} className="rounded-md border border-neutral-800 bg-neutral-900">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium hover:text-white">
        {title}
        {subtitle ? <span className="ml-2 text-xs text-neutral-500">{subtitle}</span> : null}
      </summary>
      <div className="border-t border-neutral-800 px-4 py-3">{children}</div>
    </details>
  );
}

/** Wide tables scroll inside their own box rather than the page. */
function Scroller({ children }: { children: React.ReactNode }) {
  return <div className="overflow-x-auto">{children}</div>;
}

export default function Audit() {
  const [page, setPage] = useState<AuditPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  // One instant for the whole render, so two rows on the staleness boundary
  // cannot disagree with each other.
  const [now] = useState(() => new Date());

  const load = useCallback(() => {
    setError(null);
    fetchAudit()
      .then(setPage)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  useEffect(load, [load]);

  if (error) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Audit</h1>
        <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>
        <button
          onClick={load}
          className="rounded-md bg-neutral-100 px-4 py-2 text-sm text-neutral-900 hover:bg-white"
        >
          Try again
        </button>
      </section>
    );
  }

  if (page === null) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Audit</h1>
        <p className="text-sm text-neutral-400">Loading…</p>
      </section>
    );
  }

  const counts = { current: 0, dropped: 0, never: 0 };
  for (const a of page.artists) counts[searchState(a.last_checked, now)] += 1;

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Audit</h1>
        <p className="text-sm text-neutral-400">
          {page.dismissed_count} dismissed. Review them on{" "}
          <Link to="/dismissed" className="underline hover:text-white">
            Dismissed
          </Link>
          , or drill into a pattern on{" "}
          <Link to="/patterns" className="underline hover:text-white">
            Patterns
          </Link>
          .
        </p>
      </div>

      <div className="rounded-md border border-neutral-800 bg-neutral-900 p-3 text-xs text-neutral-400">
        <span className="text-neutral-300">Search coverage:</span> {counts.current} in the
        rotation, {counts.dropped} dropped out, {counts.never} never searched by name. Only Depop
        and Poshmark search per artist, and the pool follows your Spotify top artists, so a
        dropped or never-searched artist is normally still being crawled through its retail store
        rather than being missed.
      </div>

      <Section title="Artists" subtitle={artistsSummary(page)} open>
        {page.artists.length === 0 ? (
          <p className="text-sm text-neutral-400">No artist data available.</p>
        ) : (
          <Scroller>
            <table className="w-full text-left text-xs">
              <thead className="text-neutral-500">
                <tr>
                  <th className="py-1 pr-4 font-normal">Artist</th>
                  <th className="py-1 pr-4 font-normal">Listings</th>
                  <th className="py-1 font-normal">Last search</th>
                </tr>
              </thead>
              <tbody>
                {page.artists.map((a) => {
                  const state = searchState(a.last_checked, now);
                  return (
                    <tr key={a.artist} className="border-t border-neutral-800/60">
                      <td className="py-1 pr-4 text-neutral-300">{a.artist}</td>
                      <td className="py-1 pr-4 tabular-nums text-neutral-400">{a.listings}</td>
                      {/* Dimmed rather than amber. An artist out of the search
                          rotation is the normal case, not a warning, and
                          colouring 53 rows amber trains you to ignore the
                          column. `title` carries the explanation on hover. */}
                      <td
                        title={searchStateLabel(state)}
                        className={state === "current" ? "py-1 text-neutral-500" : "py-1 text-neutral-600"}
                      >
                        {lastCheckedLabel(a.last_checked)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Scroller>
        )}
      </Section>

      <Section title="Auto-filtered" subtitle={`${page.filtered.length}`}>
        {page.filtered.length === 0 ? (
          <p className="text-sm text-neutral-400">
            Nothing auto-filtered yet. Patterns are applied on the next scraper run.
          </p>
        ) : (
          <Scroller>
            <table className="w-full text-left text-xs">
              <thead className="text-neutral-500">
                <tr>
                  <th className="py-1 pr-4 font-normal">Artist</th>
                  <th className="py-1 pr-4 font-normal">Title</th>
                  <th className="py-1 pr-4 font-normal">Source</th>
                  <th className="py-1 pr-4 font-normal">Keyword</th>
                  <th className="py-1 font-normal">Filtered</th>
                </tr>
              </thead>
              <tbody>
                {page.filtered.map((f) => (
                  <tr key={f.key} className="border-t border-neutral-800/60">
                    <td className="py-1 pr-4 text-neutral-400">{f.artist}</td>
                    <td className="py-1 pr-4 text-neutral-300">
                      {f.url ? (
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-white"
                        >
                          {f.title}
                        </a>
                      ) : (
                        f.title
                      )}
                    </td>
                    <td className="py-1 pr-4 text-neutral-500">{platformLabel(f.platform)}</td>
                    <td className="py-1 pr-4 font-mono text-neutral-500">{f.matched_pattern}</td>
                    <td className="py-1 text-neutral-500">{formatPacificDate(f.filtered_at) ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Scroller>
        )}
      </Section>

      <Section title="Learned patterns" subtitle={`${page.patterns.length}`}>
        {page.patterns.length === 0 ? (
          <p className="text-sm text-neutral-400">
            No patterns learned yet. Dismiss two or more listings from the same artist to generate
            one.
          </p>
        ) : (
          <ul className="space-y-3 text-xs">
            {page.patterns.map((p) => (
              <li key={p.artist}>
                <p className="text-sm font-medium text-neutral-200">
                  {p.artist}{" "}
                  <span className="text-xs font-normal text-neutral-500">
                    ({p.source_count} dismissal{p.source_count === 1 ? "" : "s"})
                  </span>
                </p>
                <p className="font-mono text-neutral-400">{p.keywords.join(", ")}</p>
                {p.example_titles.length > 0 ? (
                  <ul className="pt-1 text-neutral-600">
                    {p.example_titles.slice(0, 5).map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </section>
  );
}
